import { useState } from 'react';

/**
 * `useOnChange` is a custom hook that manages state and side effects related to changing values between different inputs.
 * It uses the AppStateContext to manage a side effect when the value changes.
 *
 * @param {String} initialValue - The initial value of the state.
 * @param modal initialValue - The modal property taken from AppStateContext.
 *
 * @returns {Array} An array containing the following elements:
 * - The current value of the state.
 * - A function to update the state. This function takes the new value and the old value as arguments,
 *   checks if the new value is different from the old one, and if so, it updates the `modal.setInputValueChanged` to `true`.
 *
 * @example
 * const [language, setOnChange] = useOnChange(treatedLanguage, modal);
 * ...
 * <Select
 *   onChange={(e) => setOnChange(e.target.value, language)}
 *   value={language}
 * />
 */

const useOnChange = (initialValue, modal) => {
  const [value, setValue] = useState(initialValue);

  const setOnChange = (newVal, oldVal) => {
    if (newVal !== oldVal) {
      modal.setInputValueChanged(true);
    }
    setValue(newVal);
  };

  return [value, setOnChange];
};

export default useOnChange;
